import React from 'react';
import { Link } from 'react-router-dom';
import { CONTACT, getWhatsAppUrl } from '../constants/contact';

/**
 * Mundalo Footer
 *
 * - Brand block: wordmark + tagline.
 * - Three primary nav columns aligned to product surface:
 *   THE MOVE · THE LIFE · COMPANY · LEGAL
 * - Contact strip at the bottom with email + WhatsApp.
 * - Designed to pair with the Mundalo Header (Cormorant Garamond + Outfit + JetBrains Mono).
 */
const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A1F2E] text-[#F9F6EF] mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
        {/* Brand row */}
        <div className="grid md:grid-cols-12 gap-12 md:gap-8 mb-14">
          <div className="md:col-span-4">
            <Link to="/" data-testid="footer-logo" className="inline-block mb-4">
              <span className="font-display italic text-4xl text-[#F9F6EF] leading-none">
                Mundalo
              </span>
            </Link>
            <p className="font-body text-[#F9F6EF]/70 text-sm max-w-xs leading-relaxed">
              Move Abroad. Live Freely. A guided membership for people building a
              life across borders.
            </p>
            <Link
              to="/start-trial"
              data-testid="footer-cta-start-free"
              className="inline-flex items-center gap-2 mt-6 font-mono uppercase text-[11px] tracking-[0.12em] font-semibold bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] px-5 py-2.5 rounded-full transition-all hover:-translate-y-px"
            >
              Start free - 15 days
            </Link>
          </div>

          {/* THE MOVE */}
          <div className="md:col-span-2">
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#E8A830] mb-4">
              The Move
            </p>
            <ul className="space-y-3 font-body text-sm">
              <li>
                <a href="/#how" className="text-[#F9F6EF]/80 hover:text-[#2CA8A0] transition" data-testid="footer-link-how">
                  How It Works
                </a>
              </li>
              <li>
                <a href="/#pricing" className="text-[#F9F6EF]/80 hover:text-[#2CA8A0] transition" data-testid="footer-link-pricing">
                  Pricing
                </a>
              </li>
              <li>
                <Link to="/start-trial" className="text-[#F9F6EF]/80 hover:text-[#2CA8A0] transition" data-testid="footer-link-start">
                  Start free - 15 days
                </Link>
              </li>
              <li>
                <Link to="/relocation" className="text-[#F9F6EF]/80 hover:text-[#2CA8A0] transition" data-testid="footer-link-blueprint">
                  Relocation Blueprint
                </Link>
              </li>
            </ul>
          </div>

          {/* THE LIFE */}
          <div className="md:col-span-2">
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#E8A830] mb-4">
              The Life
            </p>
            <ul className="space-y-3 font-body text-sm">
              <li>
                <Link to="/relocation/community" className="text-[#F9F6EF]/80 hover:text-[#2CA8A0] transition" data-testid="footer-link-community">
                  Community Hub
                </Link>
              </li>
              <li>
                <Link to="/relo-news" className="text-[#F9F6EF]/80 hover:text-[#2CA8A0] transition" data-testid="footer-link-news">
                  Relo News
                </Link>
              </li>
              <li>
                <Link to="/portal" className="text-[#F9F6EF]/80 hover:text-[#2CA8A0] transition" data-testid="footer-link-portal">
                  Member Portal
                </Link>
              </li>
            </ul>
          </div>

          {/* COMPANY */}
          <div className="md:col-span-2">
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#E8A830] mb-4">
              Company
            </p>
            <ul className="space-y-3 font-body text-sm">
              <li>
                <Link to="/contact" className="text-[#F9F6EF]/80 hover:text-[#2CA8A0] transition" data-testid="footer-link-contact">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/partners" className="text-[#F9F6EF]/80 hover:text-[#2CA8A0] transition" data-testid="footer-link-partners">
                  Partners
                </Link>
              </li>
              <li>
                <Link to="/become-a-partner" className="text-[#F9F6EF]/80 hover:text-[#2CA8A0] transition" data-testid="footer-link-become-partner">
                  Become a Partner
                </Link>
              </li>
            </ul>
          </div>

          {/* LEGAL */}
          <div className="md:col-span-2">
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#E8A830] mb-4">
              Legal
            </p>
            <ul className="space-y-3 font-body text-sm">
              <li>
                <Link to="/terms" className="text-[#F9F6EF]/80 hover:text-[#2CA8A0] transition" data-testid="footer-link-terms">
                  Terms
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-[#F9F6EF]/80 hover:text-[#2CA8A0] transition" data-testid="footer-link-privacy">
                  Privacy
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Contact strip */}
        <div className="grid md:grid-cols-2 gap-6 py-8 border-t border-[#F9F6EF]/10">
          <div className="font-body text-sm text-[#F9F6EF]/80">
            <a
              href={`mailto:${CONTACT.email}`}
              className="hover:text-[#2CA8A0] transition"
              data-testid="footer-email"
            >
              {CONTACT.email}
            </a>
            <span className="mx-3 text-[#F9F6EF]/30">·</span>
            <a
              href={getWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#2CA8A0] transition"
              data-testid="footer-whatsapp"
            >
              WhatsApp
            </a>
          </div>
          <p className="font-body text-sm text-[#F9F6EF]/60 md:text-right">
            Mexico City, MX · Texas, USA
          </p>
        </div>

        {/* Bottom row */}
        <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-t border-[#F9F6EF]/10">
          <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#F9F6EF]/50">
            © {year} Mundalo · Move Abroad. Live Freely.
          </p>
          <p className="font-body text-xs text-[#F9F6EF]/45 max-w-xl">
            Mundalo provides informational relocation tools and community
            access. We are not licensed immigration or tax professionals.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
